import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, RefreshCw, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Vendor dashboard widget: shows Claude's top event picks for THIS vendor,
 * each with a short reasoning sentence. Cached 24h server-side.
 */
export default function AIRecommendationsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get(`/ai/events-for-me?limit=4${refresh ? "&refresh=true" : ""}`);
      setData(data);
      setErr("");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not load recommendations";
      setErr(msg);
      if (refresh) toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  const matches = data?.matches || [];
  const strong = data?.strong_threshold ?? 78;
  const good = data?.good_threshold ?? 60;

  return (
    <div className="mt-8" data-testid="ai-recommendations-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
            <Sparkles className="h-3 w-3" /> Curated for you
          </div>
          <div className="font-serif text-3xl mt-1">Events you&apos;d love this season.</div>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Our AI reads your profile and every upcoming event, then hands you the shortlist.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing || loading}
          data-testid="ai-recommendations-refresh"
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border"
          style={{ borderColor: "rgba(0,0,0,0.15)", backgroundColor: "#fff", color: "var(--ink)" }}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Thinking…" : "Refresh"}
        </button>
      </div>

      {loading && !data && (
        <div
          data-testid="ai-recommendations-loading"
          className="mt-6 rounded-lg border-2 border-dashed p-8 text-sm text-center"
          style={{ borderColor: "rgba(0,0,0,0.06)", color: "var(--ink-soft)" }}
        >
          <Sparkles className="mx-auto h-5 w-5 animate-pulse" style={{ color: "var(--gold)" }} />
          <div className="mt-3">Reading your profile and every upcoming event…</div>
        </div>
      )}

      {err && !loading && (
        <div
          data-testid="ai-recommendations-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{ backgroundColor: "rgba(212,88,58,0.10)", color: "var(--terracotta)" }}
        >
          {err}
        </div>
      )}

      {!loading && !err && matches.length === 0 && (
        <div
          data-testid="ai-recommendations-empty"
          className="mt-6 rounded-lg border-2 border-dashed p-8 text-center text-sm"
          style={{ borderColor: "rgba(0,0,0,0.08)", color: "var(--ink-soft)" }}
        >
          No upcoming events match your profile yet. Add categories and a location on your profile to unlock better picks.
          <div className="mt-3">
            <Link to="/vendor/profile" className="underline">Edit your profile →</Link>
          </div>
        </div>
      )}

      {matches.length > 0 && (
        <div
          data-testid="ai-recommendations-list"
          className="mt-6 grid gap-4 md:grid-cols-2"
        >
          {matches.map((m) => {
            const band =
              m.score >= strong ? "strong" : m.score >= good ? "good" : "stretch";
            const bandStyles = {
              strong: { bg: "rgba(31,93,52,0.10)", color: "var(--forest)", label: "Strong fit" },
              good: { bg: "rgba(201,162,75,0.14)", color: "#a67c1e", label: "Good fit" },
              stretch: { bg: "rgba(0,0,0,0.05)", color: "var(--ink-soft)", label: "Bit of a stretch" },
            }[band];
            return (
              <Link
                key={m.event_id}
                to={`/events/${m.event_id}`}
                data-testid={`ai-recommendation-${m.event_id}`}
                className="card-warm rounded-lg p-5 block hover:shadow-md transition-shadow"
                style={{ backgroundColor: "#fff", borderLeft: `3px solid ${bandStyles.color}` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="overline" style={{ color: "var(--gold)" }}>{m.category}</div>
                    <div className="font-serif text-xl mt-1 truncate">{m.title}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
                      {m.city} · {m.date}
                      {m.booth_fee > 0 && ` · $${m.booth_fee.toFixed(0)} booth`}
                      {m.organizer_verified && (
                        <span className="ml-1 inline-flex items-center gap-0.5" style={{ color: "var(--moss)" }}>
                          <CheckCircle2 className="h-3 w-3" /> verified
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap"
                    style={{ backgroundColor: bandStyles.bg, color: bandStyles.color }}
                  >
                    {bandStyles.label} · {m.score}
                  </span>
                </div>
                <p className="mt-3 text-sm italic" style={{ color: "var(--ink-soft)" }}>
                  “{m.reasoning}”
                </p>
                <div className="mt-3 text-xs inline-flex items-center gap-1" style={{ color: "var(--terracotta)" }}>
                  View & apply <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
